import React, { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';

import { useOrderState } from '@hooks/useOrderState';
import { Header } from '@components/ModalHeader';
import { ModalButton } from '@components/ModalButton';
import { ModalContent } from '@components/ModalContent';
import { Switcher } from './components/Switcher';
import { DeliveryContainer } from './components/DeliveryContainer';
import { PickUpContainer } from './components/PickUpContainer';
import { DeliveryType } from '@interfaces';
import { getRoute, getCommuneZone, removeUndefineds } from '@utils';
import {
  setDeliveryTypeMutation,
  SetDeliveryTypeMutationResponse,
  SetDeliveryTypeMutationParams,
} from '@queries/checkout/mutations/setOrderSeliveryType';
import {
  getCheckoutAddress,
  GetCheckoutAddressQueryResponse,
  GetCheckoutAddressQueryParams,
} from '@queries/checkout/queries/address';
import { useModalRouter } from '@hooks/useModalRouter';
import { useSegment } from '@hooks/useSegment';
import { useAddressFormData } from '@screens/address/hooks/useAddressFormData';

import { ErrorTypography } from './styles/index.styles';
import { useOpenReplayTracking } from '@services/openreplay';
import { useAuth } from '@hooks/useAuth';
import { useRouter } from 'next/router';
import { ContentContainer } from '@components/ContentContainer';
import { isSafari } from '@config/environment';
import { isMobile } from 'react-device-detect';

export function CheckoutDelivery() {
  const [isDelivery, setIsDelivery] = useState(true);
  const routerModal = useModalRouter();
  const router = useRouter();
  const [deliverySelected, setDeliverySelected] = useState(false);
  const [missinInfo, setMissingInfo] = useState(false);
  const { track } = useSegment();
  const { trackEvent } = useOpenReplayTracking();
  const { appUser: user } = useAuth({ forceAuth: true });
  // Global State
  const { orderUuid } = useOrderState();
  const { setData, setQuery } = useAddressFormData();

  const {
    data: { addresses, currentOrder } = {},
    loading: loadingAddressData,
  } = useQuery<GetCheckoutAddressQueryResponse, GetCheckoutAddressQueryParams>(
    getCheckoutAddress(),
    {
      variables: { params: { orderUuid } },
    },
  );

  const dataLoaded = loadingAddressData && !currentOrder;

  const [setDeliveryType] = useMutation<
    SetDeliveryTypeMutationResponse,
    SetDeliveryTypeMutationParams
  >(setDeliveryTypeMutation(), {
    optimisticResponse({ params }) {
      return {
        setOrderDeliveryType: {
          __typename: 'Order',
          id: currentOrder?.id,
          deliveryType: params.deliveryType,
          orderGroupId: null,
        },
      };
    },
  });

  const handleDeliveryType = useCallback(
    (newDeliveryType: DeliveryType) => {
      if (newDeliveryType !== currentOrder?.deliveryType) {
        void setDeliveryType({
          variables: { params: { orderUuid, deliveryType: newDeliveryType } },
        });
      }
    },
    [currentOrder?.deliveryType, setDeliveryType, orderUuid],
  );

  const notifySelectDelivery = useCallback(
    function handleSelectAddressEvent(deliveryType: DeliveryType) {
      const commune =
        deliveryType === 'DELIVERY' ? currentOrder?.address?.commune : null;
      const zone =
        deliveryType === 'DELIVERY'
          ? getCommuneZone(currentOrder?.address?.commune)?.zoneType
          : null;
      void track('address select', {
        commune,
        zone,
        deliveryType,
      });
    },
    [currentOrder?.address?.commune, track],
  );

  const handleNext = useCallback(() => {
    if (!deliverySelected) {
      setMissingInfo(true);
      return;
    }
    if (currentOrder?.deliveryType === 'PICK_UP') {
      return void routerModal.push(getRoute('checkoutDetail'));
    }
    if (!currentOrder?.address?.isConfirmed) {
      void trackEvent('Address select missing confirmation coords', {
        address: currentOrder?.address,
      });
      const coords = currentOrder?.address?.coords;
      setData({
        ...currentOrder.address,
        metadata: coords
          ? {
              coords: {
                lat: +coords.split(',')[0],
                lng: +coords.split(',')[1],
              },
            }
          : {},
      });
      if (!coords) {
        void trackEvent('Address select missing coords', {
          address: currentOrder?.address,
        });
        setQuery(
          `${currentOrder?.address?.street} ${currentOrder?.address?.streetNumber}, ${currentOrder?.address?.commune}`,
        );
      }

      return void routerModal.push(getRoute('checkoutDetail'));
    }

    notifySelectDelivery(currentOrder?.deliveryType);
    void track('address confirm');
    return void routerModal.push(getRoute('checkoutDetail'));
  }, [
    currentOrder?.address,
    currentOrder?.deliveryType,
    deliverySelected,
    notifySelectDelivery,
    routerModal,
    setData,
    setQuery,
    track,
    trackEvent,
  ]);

  useEffect(() => {
    if (!isDelivery) {
      setDeliverySelected(true);
      setMissingInfo(false);
    }
  }, [isDelivery]);

  useEffect(
    function checkoutStart() {
      if (!currentOrder?.orderDetails?.length && !loadingAddressData) {
        return void routerModal.push(getRoute('home'));
      }
    },
    [currentOrder, loadingAddressData, orderUuid, routerModal, track],
  );

  useEffect(
    function ComponentStart() {
      void track('checkout start', {
        userId: user?.id,
        products: currentOrder?.orderDetails?.length,
      });
    },
    [currentOrder?.orderDetails?.length, track, user?.id],
  );

  useEffect(
    function handleDeliveryTypeChange() {
      void track('address options change');
    },
    [isDelivery, track],
  );

  return (
    <ContentContainer bottomNavigation={isMobile}>
      <Header
        title={'Tipo de entrega'}
        backIcon
        backFunction={() => {
          void router.push({
            pathname: router.pathname,
            query: removeUndefineds({ ...router.query, pathname: undefined }),
          });
        }}
      />
      <ModalContent isSafari={isSafari}>
        <Switcher
          isDelivery={isDelivery}
          setIsDelivery={setIsDelivery}
          handleDeliveryType={handleDeliveryType}
          setDeliverySelected={setDeliverySelected}
          currentOrder={currentOrder}
        />
        {isDelivery ? (
          <DeliveryContainer
            addresses={addresses}
            loadingAddressData={dataLoaded}
            currentOrder={currentOrder}
            setDeliverySelected={setDeliverySelected}
            missinInfo={missinInfo}
            setMissingInfo={setMissingInfo}
          />
        ) : (
          <PickUpContainer />
        )}
        {missinInfo && (
          <ErrorTypography>
            Debes seleccionar una dirección de entrega
          </ErrorTypography>
        )}
      </ModalContent>
      <ModalButton handleNext={handleNext} text={'Siguiente'} />
    </ContentContainer>
  );
}
